import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { deDateLocale, enDateLocale } from "@assets/locales/datelocale";
import NetworkError from "@pages/common/NetworkError";
import NotFound from "@pages/common/Notfound";
import Login from "@pages/Login";
import { addLocale, PrimeReactContext } from "primereact/api";
import { Suspense, useContext, useEffect, useState } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes, } from "react-router-dom";
import Spinner from "@components/others/Spinner";
import ForgotPassword from "@pages/ForgotPassword";
import ResetPassword from "@pages/ResetPassword";
import { getUserRole } from "@utils/auth";
import ProtectedRoute from "./components/common/ProtectedRoute";
import InterceptorInit from "./InterceptorInit";
import AdminLayout from "./layouts/AdminLayout";
import MandantLayout from "./layouts/MandantLayout";
import UserLayout from "./layouts/UserLayout";
import { adminRoutes } from "./routes/adminRoutes";
import { mandantRoutes } from "./routes/mandantRoutes";
import { userRoutes } from "./routes/userRoutes";
import "./styles/css/common/general.css";
/**
 * The main application component.
 * It sets up PrimeReact's ripple effect, manages authentication state,
 * and configures global date locales. It uses `BrowserRouter` for routing
 * and renders `InnerApp` within a `Suspense` boundary.
 *
 * @component
 * @author rajeev.k
 * @since 2025-08-19
 */
var App = function () {
    /**
     * Accesses `setRipple` function from PrimeReactContext to enable/disable ripple effect.
     * @type {function}
     */
    var setRipple = useContext(PrimeReactContext).setRipple;
    /**
     * State to manage the authentication status of the user, initialized from session storage.
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    var _a = useState(!!sessionStorage.getItem("token")), isAuthenticated = _a[0], setIsAuthenticated = _a[1];
    /**
     * Effect hook to enable PrimeReact's ripple effect once the component mounts.
     */
    useEffect(function () {
        if (setRipple) {
            setRipple(true);
        }
    }, [setRipple]);
    /**
     * Handles changes in session storage to update authentication status.
     * This ensures that if the token is removed (e.g., from another tab),
     * the app's authentication state is synchronized.
     */
    var handleStorageChange = function () {
        setIsAuthenticated(!!sessionStorage.getItem("token"));
    };
    /**
     * Effect hook to add and remove a storage event listener.
     */
    useEffect(function () {
        window.addEventListener("storage", handleStorageChange);
        return function () { return window.removeEventListener("storage", handleStorageChange); };
    }, []);
    // Add custom date locales for internationalization in PrimeReact components
    addLocale("en", enDateLocale);
    addLocale("de", deDateLocale);
    return (_jsxs(Router, { children: [_jsx(InterceptorInit, {}), _jsx(Suspense, { fallback: _jsx(Spinner, {}), children: _jsx(InnerApp, { isAuthenticated: isAuthenticated, setIsAuthenticated: setIsAuthenticated }) })] }));
};
/**
 * InnerApp component conditionally renders the application based on authentication status.
 * If not authenticated, it redirects to the login page.
 * If authenticated, it fetches app initialization data (user info, menu),
 * and renders the main application layout with dynamic routes.
 *
 * @component
 * @param {InnerAppProps} props - The component's properties.
 */
var InnerApp = function (_a) {
    var isAuthenticated = _a.isAuthenticated, setIsAuthenticated = _a.setIsAuthenticated;
    /**
     * Conditional rendering based on authentication status.
     * If not authenticated, always navigate to `/login`.
     */
    if (!isAuthenticated) {
        return (_jsxs(Routes, { children: [_jsx(Route, { path: "*", element: _jsx(Navigate, { to: "/login", replace: true }) }), _jsx(Route, { path: "/forgot-password", element: _jsx(ForgotPassword, {}) }), _jsx(Route, { path: "/reset-password", element: _jsx(ResetPassword, {}) }), _jsx(Route, { path: "/login", element: _jsx(Login, { setIsAuthenticated: setIsAuthenticated }) })] }));
    }
    var role = getUserRole();
    /**
     * If authenticated, render the main application routes.
     */
    return (_jsxs(Routes, { children: [_jsx(Route, { path: "/login", element: _jsx(Login, { setIsAuthenticated: setIsAuthenticated }) }), _jsx(Route, { path: "/err", element: _jsx(NetworkError, {}) }), _jsx(Route, { element: _jsx(ProtectedRoute, { allowedRoles: ["ADMIN"] }), children: _jsx(Route, { path: "/admin", element: _jsx(AdminLayout, {}), children: adminRoutes.map(function (_a) {
                        var path = _a.path, element = _a.element;
                        return (_jsx(Route, { path: path, element: element }, path));
                    }) }) }), _jsx(Route, { element: _jsx(ProtectedRoute, { allowedRoles: ["USER"] }), children: _jsx(Route, { path: "/user", element: _jsx(UserLayout, {}), children: userRoutes.map(function (_a) {
                        var path = _a.path, element = _a.element;
                        return (_jsx(Route, { path: path, element: element }, path));
                    }) }) }), _jsx(Route, { element: _jsx(ProtectedRoute, { allowedRoles: ["MANDANT"] }), children: _jsx(Route, { path: "/client", element: _jsx(MandantLayout, {}), children: mandantRoutes.map(function (_a) {
                        var path = _a.path, element = _a.element;
                        return (_jsx(Route, { path: path, element: element }, path));
                    }) }) }), _jsx(Route, { path: "*", element: _jsx(NotFound, {}) }), role === "USER" ? (_jsx(Route, { path: "/", element: _jsx(Navigate, { to: "/user/dashboard", replace: true }) })) : role === "ADMIN" ? (_jsx(Route, { path: "/", element: _jsx(Navigate, { to: "/admin/dashboard", replace: true }) })) : role === "MANDANT" ? (_jsx(Route, { path: "/", element: _jsx(Navigate, { to: "/client/dashboard", replace: true }) })) : ("")] }));
};
export default App;
